import React, { useMemo, useState } from 'react'
import { Device, Connection, SignalType } from '@/types/devices'
import { SIGNAL_TYPE_COLORS, SIGNAL_TYPE_LABELS } from '@/constants/colors'

interface ConnectionListProps {
  devices: Device[]
  connections: Connection[]
  onDeleteConnection?: (connectionId: string) => void
}

type SortField = 'source' | 'target' | 'signalType' | 'none'
type SortOrder = 'asc' | 'desc'

export const ConnectionList: React.FC<ConnectionListProps> = ({
  devices,
  connections,
  onDeleteConnection,
}) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedSignalTypes, setSelectedSignalTypes] = useState<
    Set<SignalType>
  >(
    new Set([
      'audio',
      'midi',
      'sync',
      'cv_gate',
      'adat',
      'word_clock',
      'usb',
      'ethernet',
    ]),
  )
  const [sortField, setSortField] = useState<SortField>('none')
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc')

  // Create device lookup
  const deviceMap = useMemo(() => {
    const map: Record<string, Device> = {}
    devices.forEach(d => (map[d.id] = d))
    return map
  }, [devices])

  // Create port lookup
  const portMap = useMemo(() => {
    const map: Record<string, { name: string; device: Device }> = {}
    devices.forEach(device => {
      device.inputs.forEach(port => {
        map[`${device.id}-${port.id}`] = { name: port.name, device }
      })
      device.outputs.forEach(port => {
        map[`${device.id}-${port.id}`] = { name: port.name, device }
      })
    })
    return map
  }, [devices])

  // Enrich connections with device and port names
  const enrichedConnections = useMemo(() => {
    return connections.map(conn => {
      const sourceDevice = deviceMap[conn.sourceDeviceId]
      const targetDevice = deviceMap[conn.targetDeviceId]
      const sourcePortInfo =
        portMap[`${conn.sourceDeviceId}-${conn.sourcePortId}`]
      const targetPortInfo =
        portMap[`${conn.targetDeviceId}-${conn.targetPortId}`]

      return {
        ...conn,
        sourceDeviceName: sourceDevice?.name || 'Unknown',
        targetDeviceName: targetDevice?.name || 'Unknown',
        sourcePortName: sourcePortInfo?.name || 'Unknown',
        targetPortName: targetPortInfo?.name || 'Unknown',
      }
    })
  }, [connections, deviceMap, portMap])

  // Filter connections by search query and signal type
  const filteredConnections = useMemo(() => {
    return enrichedConnections.filter(conn => {
      // Signal type filter
      if (!selectedSignalTypes.has(conn.signalType || 'audio')) return false

      // Text search filter
      const query = searchQuery.toLowerCase()
      if (query) {
        const matchesSearch =
          conn.sourceDeviceName.toLowerCase().includes(query) ||
          conn.targetDeviceName.toLowerCase().includes(query) ||
          conn.sourcePortName.toLowerCase().includes(query) ||
          conn.targetPortName.toLowerCase().includes(query)
        if (!matchesSearch) return false
      }

      return true
    })
  }, [enrichedConnections, searchQuery, selectedSignalTypes])

  // Sort connections
  const sortedConnections = useMemo(() => {
    const items = [...filteredConnections]

    if (sortField === 'none') return items

    items.sort((a, b) => {
      let aVal: string
      let bVal: string

      if (sortField === 'source') {
        aVal = a.sourceDeviceName
        bVal = b.sourceDeviceName
      } else if (sortField === 'target') {
        aVal = a.targetDeviceName
        bVal = b.targetDeviceName
      } else if (sortField === 'signalType') {
        aVal = a.signalType || 'unknown'
        bVal = b.signalType || 'unknown'
      } else {
        return 0
      }

      return sortOrder === 'asc'
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal)
    })

    return items
  }, [filteredConnections, sortField, sortOrder])

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortOrder('asc')
    }
  }

  const toggleSignalType = (signalType: SignalType) => {
    setSelectedSignalTypes(prev => {
      const next = new Set(prev)
      if (next.has(signalType)) {
        next.delete(signalType)
      } else {
        next.add(signalType)
      }
      return next
    })
  }

  const allSignalTypes: SignalType[] = [
    'audio',
    'midi',
    'sync',
    'cv_gate',
    'adat',
    'word_clock',
    'usb',
    'ethernet',
  ]

  const SortIndicator = ({
    field,
    label,
  }: {
    field: SortField
    label: string
  }) => {
    const isActive = sortField === field
    return (
      <button
        onClick={() => toggleSort(field)}
        className={`font-semibold text-xs px-3 py-2 rounded transition-colors duration-150 flex items-center gap-1 ${
          isActive
            ? 'bg-accent-muted text-accent'
            : 'text-content-secondary hover:bg-surface-sunken'
        }`}
      >
        {label}
        {isActive && (
          <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
        )}
      </button>
    )
  }

  return (
    <div className="h-full w-full flex flex-col bg-surface-base overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-brd-subtle">
        <h2 className="text-lg font-bold text-content-primary">Connections</h2>
        <p className="text-xs text-content-tertiary mt-1">
          Total: {sortedConnections.length} connection
          {sortedConnections.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Controls */}
      <div className="px-4 py-3 border-b border-brd-subtle space-y-3">
        {/* Search bar */}
        <input
          type="text"
          placeholder="Search connections..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="w-full px-3 py-2 bg-surface-sunken border border-brd-subtle rounded-lg text-sm text-content-primary placeholder-content-tertiary focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-brd-emphasis"
        />

        {/* Signal type filter pills */}
        <div className="flex gap-2 flex-wrap">
          {allSignalTypes.map(signalType => (
            <button
              key={signalType}
              onClick={() => toggleSignalType(signalType)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors duration-150 flex items-center gap-2 ${
                selectedSignalTypes.has(signalType)
                  ? 'text-white shadow-low'
                  : 'bg-surface-sunken text-content-tertiary opacity-50'
              }`}
              style={{
                backgroundColor: selectedSignalTypes.has(signalType)
                  ? SIGNAL_TYPE_COLORS[signalType]
                  : undefined,
              }}
            >
              <div className="w-2 h-2 rounded-full bg-current opacity-75" />
              {SIGNAL_TYPE_LABELS[signalType]}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {sortedConnections.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center px-4">
              <div className="text-content-tertiary text-sm font-medium mb-1">
                No connections
              </div>
              <p className="text-xs text-content-tertiary">
                {connections.length === 0
                  ? 'Add devices and create connections to see them here.'
                  : 'No connections match your filters.'}
              </p>
            </div>
          </div>
        ) : (
          <table className="w-full text-sm border-collapse">
            {/* Table header with sort controls */}
            <thead className="sticky top-0 bg-surface-sunken border-b border-brd-subtle">
              <tr>
                <th className="text-left">
                  <SortIndicator field="source" label="Source Device" />
                </th>
                <th className="text-left px-3 py-2 font-semibold text-xs text-content-secondary">
                  Port
                </th>
                <th className="text-center px-3 py-2 font-semibold text-xs text-content-tertiary">
                  →
                </th>
                <th className="text-left">
                  <SortIndicator field="target" label="Target Device" />
                </th>
                <th className="text-left px-3 py-2 font-semibold text-xs text-content-secondary">
                  Port
                </th>
                <th className="text-left">
                  <SortIndicator field="signalType" label="Type" />
                </th>
                <th className="text-center px-3 py-2 font-semibold text-xs text-content-secondary">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedConnections.map(conn => (
                <tr
                  key={conn.id}
                  className="border-b border-brd-subtle hover:bg-accent-muted transition-colors duration-150"
                  style={{
                    borderLeftWidth: '3px',
                    borderLeftColor:
                      SIGNAL_TYPE_COLORS[conn.signalType || 'audio'],
                  }}
                >
                  <td className="px-3 py-3 text-content-primary font-medium text-xs">
                    {conn.sourceDeviceName}
                  </td>
                  <td className="px-3 py-3 text-content-secondary text-xs">
                    {conn.sourcePortName}
                  </td>
                  <td className="px-3 py-3 text-center text-content-tertiary text-xs">
                    →
                  </td>
                  <td className="px-3 py-3 text-content-primary font-medium text-xs">
                    {conn.targetDeviceName}
                  </td>
                  <td className="px-3 py-3 text-content-secondary text-xs">
                    {conn.targetPortName}
                  </td>
                  <td className="px-3 py-3">
                    <div
                      className="inline-flex items-center gap-2 px-2 py-1 rounded-full text-xs font-medium text-white"
                      style={{
                        backgroundColor:
                          SIGNAL_TYPE_COLORS[conn.signalType || 'audio'],
                      }}
                    >
                      <div className="w-1.5 h-1.5 rounded-full bg-white opacity-75" />
                      {SIGNAL_TYPE_LABELS[conn.signalType || 'audio']}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <button
                      onClick={() => onDeleteConnection?.(conn.id)}
                      className="inline-flex items-center justify-center w-6 h-6 text-destructive hover:bg-destructive/10 rounded transition-colors duration-150"
                      title="Delete connection"
                    >
                      ×
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer summary */}
      <div className="bg-surface-sunken border-t border-brd-subtle px-4 py-2 text-xs text-content-secondary">
        <div className="flex justify-between items-center">
          <div>
            Showing {sortedConnections.length} of {connections.length}
          </div>
          {sortedConnections.length < connections.length && (
            <div className="text-content-tertiary">
              {connections.length - sortedConnections.length} hidden by filters
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
